import { useState } from "react";
import { MessageCircle, Mail, Phone, MapPin, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { api, track } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { toast } from "../components/ui/sonner";

export default function Contact() {
  const { settings } = useSite();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const wa = `https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent("Hi Built2Last.live — I want to discuss a project.")}`;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", {
        full_name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        project_description: form.message,
        application_type: "Other",
        lead_source: "contact_form",
      });
      setDone(true);
    } catch {
      toast.error("Submission failed. Please try again or reach us on WhatsApp.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="pt-32 lg:pt-40 pb-24" data-testid="contact-page">
      <div className="container-x">
        <p className="section-label">CONTACT</p>
        <h1 className="mt-5 font-heading text-5xl sm:text-6xl font-black tracking-tight text-white max-w-3xl">
          TALK TO <span className="text-flame">BUILT2LAST.</span>
        </h1>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="space-y-5">
            <a href={wa} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" onClick={() => track("whatsapp_click", { location: "contact" })}
              className="flex items-center gap-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 hover:bg-emerald-500/10 transition-colors">
              <MessageCircle className="w-6 h-6 text-emerald-400" />
              <div>
                <p className="font-heading font-bold text-white">WhatsApp</p>
                <p className="text-xs text-slate-400 mt-1">+{settings.whatsapp_number} — send your requirement</p>
              </div>
            </a>
            <a href={`mailto:${settings.contact_email}`} data-testid="contact-email" className="flex items-center gap-4 rounded-2xl border border-white/10 bg-navy-800 p-6 hover:border-flame/40 transition-colors">
              <Mail className="w-6 h-6 text-flame" />
              <div>
                <p className="font-heading font-bold text-white">Email</p>
                <p className="text-xs text-slate-400 mt-1">{settings.contact_email}</p>
              </div>
            </a>
            <a href={`tel:${settings.phone.replace(/\s/g, "")}`} data-testid="contact-phone" onClick={() => track("phone_click", { location: "contact" })} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-navy-800 p-6 hover:border-flame/40 transition-colors">
              <Phone className="w-6 h-6 text-flame" />
              <div>
                <p className="font-heading font-bold text-white">Phone</p>
                <p className="text-xs text-slate-400 mt-1">{settings.phone}</p>
              </div>
            </a>
            <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-navy-800 p-6">
              <MapPin className="w-6 h-6 text-flame" />
              <div>
                <p className="font-heading font-bold text-white">Office</p>
                <p className="text-xs text-slate-400 mt-1">{settings.address}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-navy-800 p-6">
              <Clock className="w-6 h-6 text-flame" />
              <div>
                <p className="font-heading font-bold text-white">Working hours</p>
                <p className="text-xs text-slate-400 mt-1">{settings.working_hours}</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-navy-800 p-8 sm:p-10">
            {done ? (
              <div className="text-center py-16" data-testid="contact-success">
                <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
                <h2 className="mt-6 font-heading text-2xl font-bold text-white">Message received.</h2>
                <p className="mt-3 text-sm text-slate-400">Our team will get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <h2 className="font-heading text-2xl font-bold text-white">Send us a message.</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Input required placeholder="Full name *" value={form.name} onChange={set("name")} data-testid="contact-name-input" className="bg-navy-900/60 border-white/10 h-12" />
                  <Input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="contact-email-input" className="bg-navy-900/60 border-white/10 h-12" />
                  <Input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" className="bg-navy-900/60 border-white/10 h-12" />
                  <Input placeholder="Company" value={form.company} onChange={set("company")} data-testid="contact-company-input" className="bg-navy-900/60 border-white/10 h-12" />
                </div>
                <Textarea required placeholder="What do you want to build? *" rows={5} value={form.message} onChange={set("message")} data-testid="contact-message-input" className="bg-navy-900/60 border-white/10" />
                <Button type="submit" disabled={busy} data-testid="contact-submit-btn" className="w-full bg-flame hover:bg-flame-hover text-white font-heading font-bold h-12 rounded-full">
                  {busy ? "SENDING…" : "SEND MESSAGE"} <ArrowRight className="w-4 h-4" />
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
